import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  selectCourseGroupGrades,
  selectNewStudentsGrades,
  tryGetStudentGrades,
  tryTicksStudentsGrades,
} from "../../../store/slices/teacher-courses";
import {
  Typography,
  Grid,
  Button,
  Drawer,
  IconButton,
  Input,
  Select,
  MenuItem,
  FormControl,
  Box,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
  Divider,
  Stack,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { NameTypography } from "../../layout/name-display";
import { IsValidZeroToTen } from "../../../services/functions/validator";
import CustomColumnMenu from "../../layout/grid-column-menu";
import StudentTeachersGradesSkeleton from "../skeleton/gradesSkeleton";
import ModalMessages from "../../layout/modal-messages";
import Spinner from "../../layout/spinner";
import { DataGrid } from "@mui/x-data-grid";

const GradesDrawer = ({
  setOpenGradesDrawer,
  setOpenDialog,
  openGradesDrawer,
  courseGroupId,
  gradesDrawerData,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [rows, setRows] = useState([]);

  const courseGroupGrades = useSelector(selectCourseGroupGrades);
  const newCourseGroupGrades = useSelector(selectNewStudentsGrades);

  useEffect(() => {
    (async () => {
      await dispatch(tryGetStudentGrades(courseGroupId));
    })();
  }, [courseGroupId, dispatch]);

  useEffect(() => {
    if (courseGroupGrades.data) {
      setRows(
        courseGroupGrades.data.map((x) => {
          return {
            nickName: x.nickName,
            score: x.score,
            studentCourseId: x.studentCourseId,
            totalClassesAttended: x.totalClassesAttended,
          };
        })
      );
    }
  }, [courseGroupGrades]);

  const handleScoreChange = (e, studentCourseId) => {
    const { value } = e.target;
    setRows((prevRows) =>
      prevRows.map((row) => {
        if (row.studentCourseId === studentCourseId) {
          let newScore = row.score;
          if (!IsValidZeroToTen(value)) {
            newScore = null;
          }
          if (IsValidZeroToTen(value)) {
            newScore = parseFloat(value);
          }
          return { ...row, score: newScore };
        }
        return row;
      })
    );
  };

  const handleScoreChangeNoNumeric = (e, studentCourseId) => {
    const { value } = e.target;
    const finalValue =
      value === 1 ? 0 : value === 2 ? 1 : value === 3 ? 10 : "";

    setRows((prevRows) =>
      prevRows.map((row) => {
        if (row.studentCourseId === studentCourseId) {
          let newScore = row.score;
          if (!IsValidZeroToTen(finalValue)) {
            newScore = null;
          }
          if (IsValidZeroToTen(finalValue)) {
            newScore = parseFloat(finalValue);
          }
          return { ...row, score: newScore };
        }
        return row;
      })
    );
  };

  const saveAttendances = async (model) => {
    const request = model.map(({ nickName, totalClassesAttended, ...rest }) => {
      return rest;
    });

    const action = await dispatch(tryTicksStudentsGrades(request));

    if (action.payload.data.isValid) {
      setOpenDialog(true);
      setOpenGradesDrawer(false);
    }
  };

  const initializeDataGrid = () => {
    return {
      columns: {
        columnVisibilityModel: {
          studentCourseId: false,
        },
      },
      pagination: { paginationModel: { pageSize: 6 } },
    };
  };

  const renderScoreInput = (row) => {
    if (gradesDrawerData.additionalDocs || gradesDrawerData.methodologyId === 1) {
      return (
        <Input
          type="number"
          name="scoreInput"
          size="small"
          value={row.score !== null && row.score !== undefined ? row.score : ""}
          onChange={(e) => handleScoreChange(e, row.studentCourseId)}
          inputProps={{
            type: "number",
            style: { textAlign: isMobile ? "right" : "center" },
          }}
          sx={{ width: isMobile ? 80 : "100%" }}
        />
      );
    }
    if (!gradesDrawerData.additionalDocs && gradesDrawerData.methodologyId === 2) {
      return (
        <FormControl variant="standard" sx={{ minWidth: isMobile ? 110 : 120 }}>
          <Select
            value={
              row.score === 0
                ? 1
                : row.score === 1
                  ? 2
                  : row.score === 10
                    ? 3
                    : ""
            }
            onChange={(e) => handleScoreChangeNoNumeric(e, row.studentCourseId)}
            fullWidth
            style={{ padding: 1, fontSize: isMobile ? 13 : "inherit" }}
          >
            <MenuItem value={1} sx={{ fontSize: isMobile ? 13 : "inherit" }}>SIN CALIFICAR</MenuItem>
            <MenuItem value={2} sx={{ fontSize: isMobile ? 13 : "inherit" }}>REPROBADO</MenuItem>
            <MenuItem value={3} sx={{ fontSize: isMobile ? 13 : "inherit" }}>APROBADO</MenuItem>
          </Select>
        </FormControl>
      );
    }
    return null;
  };

  const columns = [
    {
      field: "studentCourseId",
      headerName: "studentCourseId",
      filterable: false,
    },
    {
      field: "nickName",
      headerAlign: "center",
      align: "left",
      headerName: "Alumno",
      headerClassName: "headRow",
      width: 150,
      renderCell: (params) => (
        <span
          style={{
            whiteSpace: "normal",
            wordBreak: "break-word",
            lineHeight: 1.4,
            padding: "8px 0",
          }}
        >
          {params.value}
        </span>
      ),
    },
    {
      field: "totalClassesAttended",
      headerAlign: "center",
      align: "center",
      headerName: "Asistencia",
      headerClassName: "headRow",
      width: 100,
    },
    {
      field: "score",
      headerAlign: "center",
      align: "center",
      flex: 1,
      headerName: "Nota",
      headerClassName: "headRow",
      minWidth: 150,
      renderCell: (params) => renderScoreInput(params.row),
    },
  ];

  return (
    <>
      <Drawer
        anchor={isMobile ? "bottom" : "right"}
        open={openGradesDrawer}
        onClose={() => setOpenGradesDrawer(false)}
        PaperProps={{
          sx: {
            zIndex: 1203,
            width: { xs: "100vw", sm: 500 },
            maxWidth: "100%",
            maxHeight: { xs: "90vh", sm: "100%" },
            borderRadius: { xs: "12px 12px 0 0", sm: 0 },
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
          },
        }}
        sx={{
          zIndex: 1203,
          flexShrink: 0,
        }}
      >
        <Box sx={{ flexShrink: 0, p: 1, bgcolor: '#194B6F' }}>
          <Grid container alignItems="center">
            <Grid item xs={10}>
              <Typography
                align="left"
                sx={{
                  paddingLeft: 2,
                  paddingRight: 2,
                  paddingTop: isMobile ? 2 : 4,
                  fontWeight: 700,
                  fontSize: 18,
                  color: "white",
                }}
              >
                Calificaciones
              </Typography>
            </Grid>
            <Grid item xs={2} sx={{ textAlign: "end" }}>
              <IconButton
                sx={{ color: "white", width: 44, height: 44 }}
                onClick={() => setOpenGradesDrawer(false)}
              >
                <Close />
              </IconButton>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                paddingLeft: 2,
                paddingBottom: 2,
                display: { xs: "none", sm: "block" },
              }}
            >
              <Typography variant="body2" align="left" color="white">
                Por favor, complete información en cada campo que se solicite en el formulario
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            backgroundColor: "white",
            p: 2,
          }}
        >
          <Grid container rowSpacing={{ xs: 2, sm: 3 }} spacing={{ xs: 2, sm: 3 }}>
            {courseGroupGrades.isLoading || newCourseGroupGrades.isLoading ? (
              <StudentTeachersGradesSkeleton />
            ) : (
              <>
                <Grid item xs={12}>
                  {isMobile ? (
                    <>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ px: 1, pb: 1, borderBottom: "1px solid #e0e0e0" }}
                      >
                        <Typography sx={{ fontWeight: 700, fontSize: 13, color: "text.secondary", flex: 1 }}>
                          Alumno
                        </Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: 13, color: "text.secondary", width: 50, textAlign: "center", mr: 1 }}>
                          Asis.
                        </Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: 13, color: "text.secondary", width: 110, textAlign: "right" }}>
                          Nota
                        </Typography>
                      </Box>
                      <List disablePadding>
                        {rows.map((row, index) => (
                          <React.Fragment key={row.studentCourseId}>
                            <ListItem
                              disableGutters
                              sx={{
                                py: 1.5,
                                px: 1,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <NameTypography
                                color="text.primary"
                                sx={{
                                  wordBreak: "break-word",
                                  pr: 1,
                                  flex: 1,
                                  fontSize: 14,
                                }}
                              >
                                {row.nickName || "Alumno sin nombre"}
                              </NameTypography>
                              <Typography
                                sx={{
                                  width: 50,
                                  textAlign: "center",
                                  fontSize: 14,
                                  color: "text.secondary",
                                  mr: 1
                                }}
                              >
                                {row.totalClassesAttended}
                              </Typography>
                              <Box sx={{ width: 110, display: "flex", justifyContent: "flex-end" }}>
                                {renderScoreInput(row)}
                              </Box>
                            </ListItem>
                            {index < rows.length - 1 && <Divider />}
                          </React.Fragment>
                        ))}
                      </List>
                    </>
                  ) : (
                    <DataGrid
                      slots={{
                        columnMenu: CustomColumnMenu,
                      }}
                      autoHeight={true}
                      getRowHeight={() => "auto"}
                      rowSelection={false}
                      getRowId={(row) => row.studentCourseId}
                      rows={rows}
                      columns={columns}
                      rowsPerPageOptions={[5]}
                      initialState={initializeDataGrid()}
                    />
                  )}
                </Grid>

                <Grid item xs={12} sx={{ textAlign: "center" }}>
                  {isMobile ? (
                    <Stack direction="column" spacing={2} sx={{ mt: 1, mb: 2 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => saveAttendances(rows)}
                        disabled={
                          courseGroupGrades.isLoading ||
                          newCourseGroupGrades.isLoading
                        }
                      >
                        {(courseGroupGrades.isLoading ||
                          newCourseGroupGrades.isLoading) && (
                            <Spinner color="primary" />
                          )}
                        Guardar
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        color="error"
                        disabled={
                          courseGroupGrades.isLoading ||
                          newCourseGroupGrades.isLoading
                        }
                        onClick={() => setOpenGradesDrawer(false)}
                      >
                        Cancelar
                      </Button>
                    </Stack>
                  ) : (
                    <Box sx={{ mt: 2, mb: 2 }}>
                      <Button
                        variant="contained"
                        onClick={() => saveAttendances(rows)}
                        disabled={
                          courseGroupGrades.isLoading ||
                          newCourseGroupGrades.isLoading
                        }
                        sx={{ width: 100, marginRight: 2 }}
                      >
                        {(courseGroupGrades.isLoading ||
                          newCourseGroupGrades.isLoading) && (
                            <Spinner color="primary" />
                          )}
                        Guardar
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        disabled={
                          courseGroupGrades.isLoading ||
                          newCourseGroupGrades.isLoading
                        }
                        sx={{ border: 2, width: 100 }}
                        onClick={() => setOpenGradesDrawer(false)}
                      >
                        Cancelar
                      </Button>
                    </Box>
                  )}
                </Grid>
              </>
            )}
          </Grid>
        </Box>

        {newCourseGroupGrades.errors &&
          newCourseGroupGrades.errors.length > 0 && (
            <ModalMessages errors={newCourseGroupGrades.errors} />
          )}
        {courseGroupGrades.errors && courseGroupGrades.errors.length > 0 && (
          <ModalMessages errors={courseGroupGrades.errors} />
        )}
      </Drawer>
    </>
  );
};

export default GradesDrawer;